<script setup lang="ts">
import {watch, computed, reactive} from "vue";
import {programApi} from "@/container";
import ToggleSwitch from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleSwitch.vue";
import ToggleGroups from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleGroups.vue";
import ToggleLabel from "@/program/components/support-program/SupportProgramNotificationToggle/ToggleLabel.vue";
import useSupportProgramSettings from "@/program/composables/useSupportProgramSettings";

const props = withDefaults(defineProps<{
        supportProgramId: number,
        color?: string,
    }>(),
{color: "primary"});

const {data: settings, isLoading, mutate, updateGroup: _updateGroup} = useSupportProgramSettings(props.supportProgramId);

/**
 * Compute if notifications are on.
 */
const isOn = computed(() => {
    return settings.value?.counter_notifications !== "none";
});

const state = reactive({
    on: true,
    expanded: false,
    loading: false
});

/**
 * Update the settings.
 */
const updateSettings = async () => {
    if (!settings.value) return;

    state.loading = true;
    const updated = await programApi().updateSupportProgramSettings(settings.value.id, {
        counter_notifications: state.on ? "all" : "none",
    });

    await mutate(() => updated);

    state.loading = false;
};

/**
 * Update the settings groups.
 * @param group The group to update.
 * @param enabled The enabled state of the group.
 */
const updateGroup = async (group: string, enabled: boolean) => {
    if (!settings.value) return;

    state.loading = true;
    await _updateGroup(group, enabled);

    state.loading = false;
};

watch(isOn, (newValue) => {
    state.on = newValue;
}, {immediate: true});

watch(() => state.on, async () => {
    if (settings.value) {
        await updateSettings();
    }
}, {immediate: true});
</script>

<template>
  <div class="flex flex-col gap-1 items-end justify-center">
    <div class="flex items-center gap-4">
      <ToggleLabel v-model="state.expanded" :on="state.on" />
      <ToggleSwitch
        v-model="state.on"
        :color="color"
        :is-loading="isLoading || state.loading"
      />
    </div>
    <ToggleGroups
      v-if="state.expanded && settings && state.on"
      class="mr-2"
      :settings="settings"
      @update="updateGroup"
    />
  </div>
</template>
